import './create-isin.scss'

import { notification } from 'antd'
import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, Input, Select, SubmitButton } from 'formik-antd'
import { Moment } from 'moment'
import React from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetchByDomain } from '../../../api'
import { filterProps, parseApiErrorResponse } from '../../../components/forms/utils'
import { config } from '../../../config'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { currencies, getDefaultDomainByEnvironment } from '../../../models'
import { getEnvironment } from '../../../util/environment'
import { Issuer } from '../../Issuers/models'
import { messages } from '../messages'
import { isinFormInitialValues, IsinFormValues, isinInstrumentTypes, transformFormValuesToIsinPayload } from '../models'
import { isinValidationSchema } from '../validation'

const { isWeekendOrHoliday } = momentDateHandler

interface CreateIsinFormProps {
  onSuccess: () => void
}

export const CreateIsin = ({ onSuccess }: CreateIsinFormProps) => {
  const environment = getEnvironment()

  const { data: issuers } = useSWR<Issuer[]>(['/issuers', getDefaultDomainByEnvironment(environment)], fetchByDomain)

  const onSubmit = async (
    isinFormValues: IsinFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<IsinFormValues>,
  ) => {
    try {
      const isinPayload = transformFormValuesToIsinPayload(isinFormValues)
      await axios.post('/isins', isinPayload)

      resetForm()
      notification.success({ message: messages.common.success, description: messages.create.success })
      onSuccess()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)
      notification.error({ message: messages.common.error, description: messages.create.error })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik initialValues={isinFormInitialValues} validationSchema={isinValidationSchema} onSubmit={onSubmit}>
      {({ values, validateForm, setTouched }) => (
        <Form name="create-isin">
          <FormItem name="isin" label="ISIN" required>
            <Input name="isin" placeholder="ISIN" aria-label="ISIN" />
          </FormItem>
          <FormItem name="issuer" label="Issuer Code" required>
            <Select
              name="issuer"
              aria-label="issuer"
              placeholder="Issuer Code"
              loading={!issuers}
              showSearch
              options={issuers?.map(({ code, shortName }) => ({
                label: `${code} - ${shortName}`,
                value: code,
              }))}
              {...filterProps}
            />
          </FormItem>
          <FormItem name="type" label="Instrument Type" required>
            <Select
              name="type"
              aria-label="type"
              placeholder="Instrument Type"
              showSearch
              options={Object.values(isinInstrumentTypes).map((instrumentValue: string) => ({
                label: instrumentValue,
                value: instrumentValue,
              }))}
              {...filterProps}
            />
          </FormItem>
          <FormItem name="maturity" label="Maturity Date" required>
            <DatePicker
              name="maturity"
              aria-label="maturity"
              placeholder="Maturity Date"
              format={config.defaultDateFormat}
              disabledDate={(date: Moment) => isWeekendOrHoliday(date)}
            />
          </FormItem>
          <FormItem name="currency" label="Currency">
            <Select
              name="currency"
              aria-label="currency"
              placeholder="Select a currency"
              showSearch
              options={currencies.map((currency: string) => ({
                label: currency,
                value: currency,
              }))}
              {...filterProps}
            />
          </FormItem>
          {values.type === isinInstrumentTypes.bond && (
            <FormItem name="coupon" label="Coupon" required>
              <Input name="coupon" aria-label="coupon" placeholder="Coupon" type="number" />
            </FormItem>
          )}
          {[isinInstrumentTypes.frn, isinInstrumentTypes.frtd].includes(values.type) && (
            <>
              <FormItem name="frnIssueMargin" label="FRN Issue Margin">
                <Input
                  name="frnIssueMargin"
                  aria-label="frnIssueMargin"
                  placeholder="FRN Issue Margin"
                  type="number"
                  allowClear
                />
              </FormItem>
              <FormItem name="frnBenchmark" label="FRN Benchmark">
                <Input name="frnBenchmark" aria-label="frnBenchmark" placeholder="FRN Benchmark" allowClear />
              </FormItem>
            </>
          )}
          <Column>
            <SubmitButton htmlType="submit" type="primary">
              Create ISIN
            </SubmitButton>
          </Column>
        </Form>
      )}
    </Formik>
  )
}
